import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { MapPin, ChevronsUpDown, Loader2 } from "lucide-react";
import { useUsCities, type USCity } from "@/hooks/useUsCities";

interface CityComboboxProps {
  value: string;
  onChange: (city: string) => void;
  placeholder?: string;
}

// Shared city picker — extracted verbatim from BasicInfoSection so the profile
// and the onboarding wizard share one control and round-trip the same canonical
// "City, State" string. Emits that canonical string via onChange; callers map it
// to their own keys.
export function CityCombobox({ value, onChange, placeholder = "Search city..." }: CityComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { cities, isLoading } = useUsCities();

  // Filter cities based on search input (min 2 chars, max 10 results)
  const filteredCities = (() => {
    if (search.length < 2) return [];
    const lower = search.toLowerCase();
    const prefixMatches: USCity[] = [];
    const containsMatches: USCity[] = [];
    for (const entry of cities) {
      if (prefixMatches.length + containsMatches.length >= 10) break;
      const cityLower = entry.city.toLowerCase();
      if (cityLower.startsWith(lower)) {
        prefixMatches.push(entry);
      } else if (cityLower.includes(lower)) {
        containsMatches.push(entry);
      }
    }
    return [...prefixMatches, ...containsMatches].slice(0, 10);
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal text-left">
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type at least 2 characters..." value={search} onValueChange={setSearch} />
          <CommandList>
            {search.length >= 2 && isLoading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading cities...
              </div>
            )}
            {search.length >= 2 && !isLoading && filteredCities.length === 0 && (
              <CommandEmpty>
                <div className="p-2 text-sm">
                  <p className="text-muted-foreground mb-2">No city found.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      onChange(search);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    Use "{search}"
                  </Button>
                </div>
              </CommandEmpty>
            )}
            {search.length < 2 && (
              <div className="py-6 text-center text-sm text-muted-foreground">Type at least 2 characters to search</div>
            )}
            {filteredCities.length > 0 && (
              <CommandGroup>
                {filteredCities.map((entry, idx) => (
                  <CommandItem
                    key={`${entry.city}-${entry.state}-${idx}`}
                    value={`${entry.city}-${entry.state}`}
                    onSelect={() => {
                      onChange(`${entry.city}, ${entry.state}`);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <MapPin className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                    {entry.city}, {entry.state}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
